import { isTauri } from '@/lib/offline/isTauri';

export const ROOT_DOMAIN = 'skygis.app';
const RESERVED_SUBDOMAINS = ['app', 'api', 'www', 'docs', 'resources', 'dev', 'local'];
const ENV_SUBDOMAINS = ['dev', 'local'];
const INTENDED_ORG_COOKIE = 'skygis.intendedOrg';
const INTENDED_ORG_MAX_AGE_SECONDS = 15 * 60;

/**
 * Tauri serves the whole app from a single origin (`tauri://localhost`) — there
 * are no org subdomains, and navigating to one (e.g. `tauri://acme.skygis.app`)
 * lands on an origin where no bundled assets exist, breaking the app. So on
 * Tauri the active org slug is carried in localStorage instead of the hostname,
 * and all org/app navigation stays on the served origin.
 */
const TAURI_ORG_KEY = 'tauri.orgSlug';

const getTauriOrgSlug = (): string | null => {
  try {
    return localStorage.getItem(TAURI_ORG_KEY) || null;
  } catch {
    return null;
  }
};

export const CUSTOM_DOMAIN_CACHE_KEY = '@customDomain';

export interface CustomDomainOrg {
  hostname: string;
  organizationId: string;
  slug: string;
  logoUrl: string | null;
}

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '0.0.0.0'];

// A hostname that is neither ours (skygis.app), local dev, nor Tauri is a
// (potential) customer custom domain.
export const isCustomDomain = (hostname = window.location.hostname): boolean => {
  if (isTauri()) return false;
  if (hostname === ROOT_DOMAIN || hostname.endsWith(`.${ROOT_DOMAIN}`)) return false;
  if (LOCAL_HOSTNAMES.includes(hostname) || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return false;
  return true;
};

export const getCachedCustomDomainOrg = (hostname = window.location.hostname): CustomDomainOrg | null => {
  try {
    const raw = localStorage.getItem(CUSTOM_DOMAIN_CACHE_KEY);
    if (!raw) return null;
    const cached: CustomDomainOrg = JSON.parse(raw);
    return cached.hostname === hostname ? cached : null;
  } catch {
    return null;
  }
};

// Base domain that is correct even on a foreign hostname, where the hostname
// itself carries no env info: derive it from the API endpoint the bundle was
// built with (api.dev.skygis.app -> dev.skygis.app).
export const getEnvBaseDomain = (): string => {
  if (!isCustomDomain()) return getBaseDomain();
  try {
    return new URL(import.meta.env.VITE_ENDPOINT).hostname.replace(/^api\./, '');
  } catch {
    return ROOT_DOMAIN;
  }
};

export const getBaseDomain = (hostname = window.location.hostname): string => {
  // Detect environment prefix (e.g. "dev", "local") from the hostname
  // so that actegon.dev.skygis.app → dev.skygis.app, not skygis.app
  if (hostname.endsWith(`.${ROOT_DOMAIN}`)) {
    const prefix = hostname.slice(0, -(ROOT_DOMAIN.length + 1));
    const parts = prefix.split('.');
    const envPart = parts[parts.length - 1];
    if (envPart && ENV_SUBDOMAINS.includes(envPart)) {
      return `${envPart}.${ROOT_DOMAIN}`;
    }
  }
  return ROOT_DOMAIN;
};

export const getOrgSlugFromHostname = (hostname = window.location.hostname): string | null => {
  // On Tauri the org isn't a subdomain — read the persisted slug.
  if (isTauri()) return getTauriOrgSlug();

  if (isCustomDomain(hostname)) return getCachedCustomDomainOrg(hostname)?.slug ?? null;

  const baseDomain = getBaseDomain(hostname);
  if (!hostname.endsWith(baseDomain)) return null;

  const prefix = hostname.slice(0, -(baseDomain.length + 1)); // strip ".baseDomain"
  if (!prefix || RESERVED_SUBDOMAINS.includes(prefix)) return null;

  return prefix;
};

const getCookieValue = (name: string): string | null => {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  return match.split('=').slice(1).join('=') || null;
};

const getRootCookieDomain = () => {
  const parts = getBaseDomain().split('.');
  return parts.slice(-2).join('.');
};

const clearIntendedOrgCookie = () => {
  document.cookie = `${INTENDED_ORG_COOKIE}=; Domain=.${getRootCookieDomain()}; Path=/; Secure; SameSite=Lax; Max-Age=0`;
};

export const rememberIntendedOrgFromHostname = (hostname = window.location.hostname): string | null => {
  const slug = getOrgSlugFromHostname(hostname);
  if (!slug || isCustomDomain(hostname)) return null;

  const payload = encodeURIComponent(
    JSON.stringify({
      slug,
      baseDomain: getBaseDomain(hostname),
      createdAt: Date.now(),
    }),
  );
  document.cookie = `${INTENDED_ORG_COOKIE}=${payload}; Domain=.${getRootCookieDomain()}; Path=/; Secure; SameSite=Lax; Max-Age=${INTENDED_ORG_MAX_AGE_SECONDS}`;
  return slug;
};

export const consumeIntendedOrgSlug = (allowedSlugs?: string[]): string | null => {
  try {
    const raw = getCookieValue(INTENDED_ORG_COOKIE);
    clearIntendedOrgCookie();
    if (!raw) return null;

    const payload = JSON.parse(decodeURIComponent(raw)) as {
      slug?: string;
      baseDomain?: string;
      createdAt?: number;
    };
    if (!payload.slug || payload.baseDomain !== getBaseDomain()) return null;
    if (!payload.createdAt || Date.now() - payload.createdAt > INTENDED_ORG_MAX_AGE_SECONDS * 1000) return null;
    if (allowedSlugs && !allowedSlugs.includes(payload.slug)) return null;
    return payload.slug;
  } catch {
    clearIntendedOrgCookie();
    return null;
  }
};

export const getPreferredOrgSlug = (allowedSlugs?: string[]): string | null => {
  const currentSlug = getOrgSlugFromHostname();
  if (currentSlug && (!allowedSlugs || allowedSlugs.includes(currentSlug))) return currentSlug;
  return consumeIntendedOrgSlug(allowedSlugs);
};

export const getOrgIdFromHostname = (hostname = window.location.hostname): string | null => {
  if (isCustomDomain(hostname)) return getCachedCustomDomainOrg(hostname)?.organizationId ?? null;

  const slug = getOrgSlugFromHostname(hostname);
  if (!slug) return null;

  try {
    const raw = localStorage.getItem('@organizations');
    if (!raw) return null;
    const orgs: Array<{ id: string; slug?: string | null }> = JSON.parse(raw);
    return orgs.find((o) => o.slug === slug)?.id ?? null;
  } catch {
    return null;
  }
};

export const isAppDomain = (hostname = window.location.hostname): boolean => {
  return getOrgSlugFromHostname(hostname) === null;
};

export const buildOrgUrl = (slug: string, path = '/'): string => {
  if (isTauri()) {
    // Persist the org and stay on tauri://localhost so assets keep resolving.
    // (Navigating to `tauri://<slug>.skygis.app` would 404 every asset.)
    try {
      localStorage.setItem(TAURI_ORG_KEY, slug);
    } catch {
      /* ignore */
    }
    return `${window.location.origin}${path}`;
  }
  const baseDomain = getEnvBaseDomain();
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';
  return `${protocol}//${slug}.${baseDomain}${port}${path}`;
};

export const buildAppUrl = (path = '/'): string => {
  if (isTauri()) {
    // Leaving an org → clear the persisted slug, stay on the served origin.
    try {
      localStorage.removeItem(TAURI_ORG_KEY);
    } catch {
      /* ignore */
    }
    return `${window.location.origin}${path}`;
  }
  const baseDomain = getEnvBaseDomain();
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';
  return `${protocol}//app.${baseDomain}${port}${path}`;
};
